import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Image, StyleSheet, Text, View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';
import { useDispatch, useSelector } from 'react-redux';

import ArcadeArtwork from '../../assets/vipRooms/vip-arcade.png';
import ArcadeFeatureIcon from '../../assets/vipRooms/vip-arcade-feature-icon.png';
import ClubArtwork from '../../assets/vipRooms/vip-club.png';
import ClubFeatureIcon from '../../assets/vipRooms/vip-club-feature-icon.png';
import VipTableStageArt from '../../assets/vipRooms/vip-table-stage.jpg';
import { getSocket } from '../../services/socket';
import { RootState } from '../../store';
import { selectIsLogged } from '../../store/auth/authReducer';
import { lobbyRoomsRequest } from '../../store/lobby/lobbyActions';
import { TableItem } from '../../store/types';
import { useSafeViewport } from '../../utils/hooks/useSafeViewport';
import { useSvgId } from '../../utils/hooks/useSvgId';
import { CarouselNav } from '../Components/common/CarouselNav';
import { CarouselStage, STAGE, useCarouselStage } from '../Components/common/carouselStage';
import { RecentTablesBar } from '../Components/common/RecentTablesBar';
import { Pressable } from '../Components/common/Touchable';
import { getDesktopColumns } from '../Components/Desktop/DesktopBottomBars';
import { DesktopCoverflow } from '../Components/Desktop/DesktopCoverflow';
import { DesktopJackpotBar } from '../Components/Desktop/DesktopJackpotBar';
import { useDesktopLayout } from '../Components/Desktop/desktopLayout';
import { DesktopRecentTablesBar } from '../Components/Desktop/DesktopRecentTablesBar';
import { TableCard } from '../Components/LobbyTable/TableCard';
import {
	RoomTablesCarousel,
	RoomTablesCarouselHandle,
} from '../Components/Middle/RoomTablesCarousel';
import BecomeVipModal from '../Components/Modals/BecomeVipModal';
import VipTablesModal from '../Components/Modals/VipTablesModal';
import { RootStackParamList } from '../navigator';
import { MainLayout } from '../Pages/MainLayout';

/** Server events that change the VIP room's table list. */
const LOBBY_CHANGE_EVENTS = [
	'tableCreatedPublisher',
	'getClosedTables',
	'joinTablePublisher',
	'exitTablePublisher',
];

const CARD_ASPECT = 416 / 226;
// The phone page on the carousel stage (common/carouselStage, Figma 8526:40066): under the dots,
// the create button at 522, its caption at 576, the VIP Arcade / VIP Club pills at 602 and the
// recent-tables bar at 659, which ends at the stage's 744.
const CREATE_BUTTON = { top: 522, width: 328, height: 48 };
const CAPTION_TOP = 576;
const FEATURE_ROW = { top: 602, width: 358, gap: 10, height: 45 };
const RECENT_TABLES = { top: 659, width: 358 };
// Room around the front card for its glow, which the carousel would otherwise clip.
const CARD_GLOW_ROOM = 16;
// The create button's gold fill, top to bottom.
const CREATE_FILL_STOPS: Array<[string, string]> = [
	['0', '#FFFF9E'],
	['0.4', '#FFF908'],
	['0.92', '#F58F00'],
	['1', '#B25200'],
];
const SETTLE_MS = 500;

type Nav = NativeStackNavigationProp<RootStackParamList>;

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const VipFeatureCard = ({
	title,
	subtitle,
	artwork,
	icon,
	onPress,
	width,
}: {
	title: string;
	subtitle: string;
	artwork: number;
	icon: number;
	onPress: () => void;
	width: number;
}) => {
	const scale = width / 174;
	const height = 160 * scale;

	return (
		<Pressable
			accessibilityRole='button'
			onPress={onPress}
			style={[styles.featureCard, { width, height }]}
		>
			<Image
				source={artwork}
				resizeMode='cover'
				style={[
					styles.featureArtwork,
					{
						left: 4 * scale,
						top: 40 * scale,
						width: 166 * scale,
						height: 114 * scale,
						borderRadius: 6 * scale,
					},
				]}
			/>
			<View
				style={[
					styles.featureSurface,
					{
						left: scale,
						top: 2 * scale,
						width: 173 * scale,
						height: 83 * scale,
						borderRadius: 8 * scale,
					},
				]}
			/>
			<View
				style={[
					styles.featureCopy,
					{ left: 8 * scale, top: 6 * scale, width: 158 * scale, height: 51 * scale },
				]}
			>
				<View style={[styles.featureTitleRow, { height: 23 * scale }]}>
					<Image
						source={icon}
						resizeMode='contain'
						style={{ width: 30 * scale, height: 20 * scale }}
					/>
					<Text
						numberOfLines={1}
						style={[
							styles.featureTitle,
							{ width: 95 * scale, fontSize: 18 * scale, lineHeight: 23.4 * scale },
						]}
					>
						{title}
					</Text>
				</View>
				<Text style={[styles.featureSubtitle, { fontSize: 12 * scale, lineHeight: 14 * scale }]}>
					{subtitle}
				</Text>
			</View>
		</Pressable>
	);
};

/** VIP Arcade / VIP Club on the phone stage: a 174x45 pill, the artwork behind a one-line title. */
const VipFeaturePill = ({
	title,
	artwork,
	icon,
	onPress,
	width,
}: {
	title: string;
	artwork: number;
	icon: number;
	onPress: () => void;
	width: number;
}) => {
	const scale = width / 174;

	return (
		<Pressable
			accessibilityRole='button'
			onPress={onPress}
			style={[
				styles.featurePill,
				{ width, height: FEATURE_ROW.height * scale, borderRadius: 8 * scale },
			]}
		>
			<Image
				source={artwork}
				resizeMode='cover'
				style={[styles.featurePillArtwork, { top: -50 * scale, width, height: 124 * scale }]}
			/>
			<View style={[styles.featurePillShade, { borderRadius: 8 * scale }]} />
			<View style={[styles.featureTitleRow, styles.featurePillCopy]}>
				<Image
					source={icon}
					resizeMode='contain'
					style={{ width: 30 * scale, height: 20 * scale }}
				/>
				<Text
					numberOfLines={1}
					style={[styles.featureTitle, { fontSize: 18 * scale, lineHeight: 23.4 * scale }]}
				>
					{title}
				</Text>
			</View>
		</Pressable>
	);
};

/** "Create VIP table" on the phone stage: the design's gold primary button. */
const CreateTableButton = ({
	label,
	onPress,
	scale,
}: {
	label: string;
	onPress: () => void;
	scale: number;
}) => {
	const fillId = useSvgId('vipCreateFill');
	const width = CREATE_BUTTON.width * scale;
	const height = CREATE_BUTTON.height * scale;
	const radius = 12 * scale;
	const iconSize = 24 * scale;

	return (
		<Pressable
			accessibilityRole='button'
			onPress={onPress}
			style={[styles.createGoldButton, { width, height, borderRadius: radius }]}
		>
			<Svg width={width} height={height} style={StyleSheet.absoluteFill}>
				<Defs>
					<LinearGradient id={fillId} x1='0' y1='0' x2='0' y2='1'>
						{CREATE_FILL_STOPS.map(([offset, color]) => (
							<Stop key={offset} offset={offset} stopColor={color} />
						))}
					</LinearGradient>
				</Defs>
				<Rect x={0} y={0} width={width} height={height} rx={radius} fill={`url(#${fillId})`} />
			</Svg>
			<View
				style={[
					styles.createPlus,
					{ width: iconSize, height: iconSize, borderRadius: iconSize / 2 },
				]}
			>
				<Text style={[styles.createPlusText, { fontSize: 18 * scale, lineHeight: iconSize }]}>
					+
				</Text>
			</View>
			<Text numberOfLines={1} style={[styles.createGoldText, { fontSize: 20 * scale }]}>
				{label}
			</Text>
		</Pressable>
	);
};

/** Lays the stage out inside MainLayout, under the header offset it provides. */
const StageBody = ({ render }: { render: (stage: CarouselStage) => React.ReactNode }) => (
	<>{render(useCarouselStage())}</>
);

const VipRoomsScreen = () => {
	const { t } = useTranslation();
	const { width } = useSafeViewport();
	const dispatch = useDispatch();
	const navigation = useNavigation<Nav>();
	const carouselRef = useRef<RoomTablesCarouselHandle>(null);
	const activeIndexRef = useRef(0);
	const commitIndexTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const carouselProgress = useSharedValue(0);
	const [activeIndex, setActiveIndex] = useState(0);
	const [dotIndex, setDotIndex] = useState(0);
	const [tables, setTables] = useState<TableItem[]>([]);
	const [tablesLoading, setTablesLoading] = useState(true);
	const [showCreateTable, setShowCreateTable] = useState(false);
	const [showBecomeVip, setShowBecomeVip] = useState(false);

	const desktop = useDesktopLayout();
	const vipRoom = useSelector((state: RootState) => state.lobby.vipRoom);
	const roomsLoaded = useSelector((state: RootState) => state.lobby.loaded);
	const roomsLoading = useSelector((state: RootState) => state.lobby.loading);
	const roomsError = useSelector((state: RootState) => state.lobby.error);
	const user = useSelector((state: RootState) => state.auth.user);
	const isLogged = useSelector(selectIsLogged);

	useEffect(() => {
		if (!roomsLoaded && !roomsLoading && !roomsError) {
			dispatch(lobbyRoomsRequest({ start: 0, limit: 20 }));
		}
	}, [dispatch, roomsError, roomsLoaded, roomsLoading]);

	useEffect(() => {
		if (!vipRoom) return;

		const playerId = user?.id || 'anonymous';
		const username = user?.username || 'anonymous';
		const socket = getSocket(playerId, username);
		const requestTables = () => socket.emit('service:getTables', { roomId: vipRoom.id });
		const handleTables = (packet: any) => {
			const items =
				(Array.isArray(packet?.data) && packet.data) ||
				(Array.isArray(packet?.items) && packet.items) ||
				(Array.isArray(packet) && packet) ||
				[];
			const vipTables = items.filter(
				(table: TableItem) => String(table.room_id) === String(vipRoom.id),
			);
			// The socket is shared: a reply for another room's list is not ours to show.
			if (items.length > 0 && vipTables.length === 0) return;

			console.log('[VIP_ROOMS] getTables response', { roomId: vipRoom.id, items, vipTables });
			setTables(vipTables);
			setTablesLoading(false);
		};

		setTablesLoading(true);
		if (socket.connected) requestTables();
		else socket.once('connect', requestTables);
		socket.on('getTables', handleTables);

		// The server tells this room's lobby watchers (`lobby:<roomId>`, joined by the request above)
		// when a table is created or closed or a seat changes. The payload only names the table, so
		// refetch the listing; debounced so a burst produces one request.
		let refetchTimer: ReturnType<typeof setTimeout> | null = null;
		const handleLobbyChange = (packet: any) => {
			const eventRoomId = packet?.data?.roomID ?? packet?.roomID;
			if (eventRoomId != null && String(eventRoomId) !== String(vipRoom.id)) return;
			if (refetchTimer) clearTimeout(refetchTimer);
			refetchTimer = setTimeout(() => {
				refetchTimer = null;
				if (socket.connected) requestTables();
			}, 300);
		};
		LOBBY_CHANGE_EVENTS.forEach(event => socket.on(event, handleLobbyChange));

		return () => {
			if (refetchTimer) clearTimeout(refetchTimer);
			socket.off('connect', requestTables);
			socket.off('getTables', handleTables);
			LOBBY_CHANGE_EVENTS.forEach(event => socket.off(event, handleLobbyChange));
			socket.emit('service:leaveLobbyRoom', { roomId: vipRoom.id });
		};
	}, [user?.id, user?.username, vipRoom]);

	useEffect(() => {
		if (roomsLoaded && !vipRoom) setTablesLoading(false);
	}, [roomsLoaded, vipRoom]);

	useEffect(() => {
		if (!vipRoom || !tablesLoading) return;

		const timeoutId = setTimeout(() => setTablesLoading(false), 3000);
		return () => clearTimeout(timeoutId);
	}, [tablesLoading, vipRoom]);

	useEffect(() => {
		const safeIndex = clamp(activeIndex, 0, Math.max(0, tables.length - 1));
		if (safeIndex !== activeIndex) {
			activeIndexRef.current = safeIndex;
			setActiveIndex(safeIndex);
			setDotIndex(safeIndex);
			carouselRef.current?.scrollToIndex(safeIndex);
		}
	}, [activeIndex, tables.length]);

	useEffect(
		() => () => {
			if (commitIndexTimerRef.current) clearTimeout(commitIndexTimerRef.current);
		},
		[],
	);

	const handleActiveIndexChange = useCallback((nextIndex: number) => {
		activeIndexRef.current = nextIndex;
		setDotIndex(nextIndex);
		if (commitIndexTimerRef.current) clearTimeout(commitIndexTimerRef.current);
		commitIndexTimerRef.current = setTimeout(() => setActiveIndex(nextIndex), SETTLE_MS);
	}, []);

	const handleCreatePress = useCallback(() => {
		if (!isLogged) {
			navigation.navigate('Login');
			return;
		}
		if (user?.isVip) setShowCreateTable(true);
		else setShowBecomeVip(true);
	}, [isLogged, navigation, user?.isVip]);

	// Every player sees the VIP tables; one without VIP is offered it on tapping one.
	const openBecomeVip = useCallback(() => setShowBecomeVip(true), []);

	if (desktop.isDesktopLayout) {
		// The desktop frame in the desktop home's two columns: the VIP tables flat along the left
		// column over the recent-tables bar, and on the right the two feature cards and the create
		// button over the jackpot - the bars at the home's own 510.
		const d = desktop.s;
		const columns = getDesktopColumns(desktop.contentWidth, desktop.scale);
		const blockTop = Math.max(0, (desktop.contentHeight - d(592)) / 2);
		const tableHeight = d(420);
		const tableWidth = tableHeight / CARD_ASPECT;
		const featureWidth = (columns.right - d(16)) / 2;
		// The mobile screen's 328x48 button and 12 copy, at the desktop scale of 1.62.
		const k = desktop.scale * 1.62;

		return (
			<MainLayout fullBleed>
				<View
					style={[styles.desktopRoot, { paddingHorizontal: desktop.side, paddingTop: blockTop }]}
				>
					<View style={[styles.desktopColumns, { columnGap: columns.gap, height: d(592) }]}>
						<View style={{ width: columns.left }}>
							<View style={{ marginTop: d(8), height: tableHeight }}>
								{tablesLoading ? (
									<View style={styles.carouselFallback}>
										<ActivityIndicator size='large' color='#FFCC33' />
									</View>
								) : tables.length ? (
									<DesktopCoverflow
										items={tables}
										keyOf={(table, index) => String(table.id ?? index)}
										width={columns.left}
										cardWidth={tableWidth}
										cardHeight={tableHeight}
										scale={desktop.scale}
										initialIndex={Math.floor((tables.length - 1) / 2)}
										renderItem={(table, index) => (
											<TableCard
												table={table}
												roomSlug='vip'
												index={index}
												totalCount={tables.length}
												cardWidth={tableWidth}
												cardHeight={tableHeight}
												framed
												onVipRequired={openBecomeVip}
											/>
										)}
									/>
								) : (
									<View style={styles.carouselFallback}>
										<Text style={[styles.emptyText, { fontSize: d(20) }]}>
											{t('T_SITE_VIP_NO_TABLES', { defaultValue: 'No VIP tables available' })}
										</Text>
									</View>
								)}
							</View>
							<View style={[styles.desktopBar, { top: d(510) }]}>
								<DesktopRecentTablesBar
									width={columns.left}
									scale={desktop.scale}
									onPress={() => navigation.navigate(isLogged ? 'MyTables' : 'Login')}
								/>
							</View>
						</View>

						<View style={{ width: columns.right, alignItems: 'center' }}>
							<View style={[styles.featureRow, { marginTop: d(8), columnGap: d(16) }]}>
								<VipFeatureCard
									title={t('T_SITE_ACHIEVEMENT_CATEGORY_ARCADE', {
										defaultValue: 'VIP ARCADE',
									}).toUpperCase()}
									subtitle={t('T_SITE_VIP_BONUS_GAMES', {
										defaultValue: 'EXCLUSIVE BONUS GAMES\nFOR VIP MEMBERS',
									})}
									artwork={ArcadeArtwork}
									icon={ArcadeFeatureIcon}
									width={featureWidth}
									onPress={() => navigation.navigate('Bonuses')}
								/>
								<VipFeatureCard
									title={t('T_SITE_VIP_CLUB', { defaultValue: 'VIP CLUB' })}
									subtitle={t('T_SITE_VIP_HIGHER_LEVELS', {
										defaultValue: 'HIGHER LEVELS.\nBIGGER BENEFITS.',
									})}
									artwork={ClubArtwork}
									icon={ClubFeatureIcon}
									width={featureWidth}
									onPress={() => navigation.navigate('VipSubscription')}
								/>
							</View>

							<Pressable
								accessibilityRole='button'
								onPress={handleCreatePress}
								style={[
									styles.createButton,
									{
										width: columns.right,
										height: 48 * k,
										marginTop: d(28),
										borderRadius: 12 * k,
									},
								]}
							>
								<Text style={[styles.createButtonText, { fontSize: 18 * k }]}>
									{t('T_SITE_VIP_CREATE_TABLE', { defaultValue: 'CREATE VIP TABLE' })}
								</Text>
							</Pressable>

							<Text style={[styles.invitationText, { fontSize: 12 * k, marginTop: d(14) }]}>
								{t('T_SITE_VIP_CREATE_TABLE_DESC', {
									defaultValue: 'Create your own private VIP table and invite your friends',
								})}
							</Text>

							<View style={[styles.desktopBar, { top: d(510) }]}>
								<DesktopJackpotBar width={columns.right} scale={desktop.scale} />
							</View>
						</View>
					</View>
				</View>

				{showCreateTable ? <VipTablesModal onClose={() => setShowCreateTable(false)} /> : null}
				{showBecomeVip ? <BecomeVipModal onClose={() => setShowBecomeVip(false)} /> : null}
			</MainLayout>
		);
	}

	const renderStage = (stage: CarouselStage) => {
		const { s: ss, y } = stage;
		const glowRoom = ss(CARD_GLOW_ROOM);
		const carouselHeight = stage.cardHeight + glowRoom * 2;

		return (
			<View style={styles.page} pointerEvents='box-none'>
				<View style={[styles.block, { top: y(STAGE.cardTop) - glowRoom, height: carouselHeight }]}>
					{tablesLoading ? (
						<View style={[styles.carouselFallback, { width }]}>
							<ActivityIndicator size='large' color='#FFCC33' />
						</View>
					) : tables.length ? (
						<RoomTablesCarousel
							ref={carouselRef}
							tables={tables}
							roomSlug='vip'
							activeIndex={activeIndex}
							onActiveIndexChange={handleActiveIndexChange}
							width={width}
							height={carouselHeight}
							cardWidth={stage.cardWidth}
							cardHeight={stage.cardHeight}
							progress={carouselProgress}
							onVipRequired={openBecomeVip}
							shape='stage'
							cardBackground={VipTableStageArt}
						/>
					) : (
						<View style={[styles.carouselFallback, { width }]}>
							<Text style={[styles.emptyText, { fontSize: ss(16) }]}>
								{t('T_SITE_VIP_NO_TABLES', { defaultValue: 'No VIP tables available' })}
							</Text>
						</View>
					)}
				</View>

				{tables.length ? (
					<View style={[styles.block, { top: y(STAGE.dotsTop) }]}>
						<CarouselNav
							count={tables.length}
							activeIndex={dotIndex}
							progress={carouselProgress}
							onDotPress={index => {
								setDotIndex(index);
								carouselRef.current?.scrollToIndex(index);
							}}
							scale={stage.scale}
						/>
					</View>
				) : null}

				<View style={[styles.block, { top: y(CREATE_BUTTON.top) }]}>
					<CreateTableButton
						label={t('T_SITE_VIP_CREATE_TABLE', { defaultValue: 'CREATE VIP TABLE' })}
						onPress={handleCreatePress}
						scale={stage.scale}
					/>
				</View>

				<View style={[styles.block, { top: y(CAPTION_TOP) }]}>
					<Text
						numberOfLines={1}
						adjustsFontSizeToFit
						style={[
							styles.invitationText,
							{ width: ss(FEATURE_ROW.width), fontSize: ss(12), lineHeight: ss(14) },
						]}
					>
						{t('T_SITE_VIP_CREATE_TABLE_DESC', {
							defaultValue: 'Create your own private VIP table and invite your friends',
						})}
					</Text>
				</View>

				<View style={[styles.block, { top: y(FEATURE_ROW.top) }]}>
					<View
						style={[
							styles.featureRow,
							{ width: ss(FEATURE_ROW.width), columnGap: ss(FEATURE_ROW.gap) },
						]}
					>
						<VipFeaturePill
							title={t('T_SITE_ACHIEVEMENT_CATEGORY_ARCADE', {
								defaultValue: 'VIP ARCADE',
							}).toUpperCase()}
							artwork={ArcadeArtwork}
							icon={ArcadeFeatureIcon}
							width={ss((FEATURE_ROW.width - FEATURE_ROW.gap) / 2)}
							onPress={() => navigation.navigate('Bonuses')}
						/>
						<VipFeaturePill
							title={t('T_SITE_VIP_CLUB', { defaultValue: 'VIP CLUB' })}
							artwork={ClubArtwork}
							icon={ClubFeatureIcon}
							width={ss((FEATURE_ROW.width - FEATURE_ROW.gap) / 2)}
							onPress={() => navigation.navigate('VipSubscription')}
						/>
					</View>
				</View>

				<View style={[styles.block, { top: y(RECENT_TABLES.top) }]}>
					<RecentTablesBar
						width={ss(RECENT_TABLES.width)}
						onPress={() => navigation.navigate(isLogged ? 'MyTables' : 'Login')}
					/>
				</View>
			</View>
		);
	};

	return (
		<MainLayout fullBleed>
			<StageBody render={renderStage} />

			{showCreateTable ? <VipTablesModal onClose={() => setShowCreateTable(false)} /> : null}
			{showBecomeVip ? <BecomeVipModal onClose={() => setShowBecomeVip(false)} /> : null}
		</MainLayout>
	);
};

const styles = StyleSheet.create({
	page: {
		...StyleSheet.absoluteFillObject,
	},
	// Each block of the phone page hangs at its stage y, centred across the screen.
	block: {
		position: 'absolute',
		left: 0,
		right: 0,
		alignItems: 'center',
	},
	featurePill: {
		overflow: 'hidden',
		borderWidth: 1,
		borderColor: '#FFB21A',
		backgroundColor: '#501006',
		justifyContent: 'center',
	},
	featurePillArtwork: {
		position: 'absolute',
		left: 0,
	},
	featurePillShade: {
		...StyleSheet.absoluteFillObject,
		backgroundColor: 'rgba(40, 4, 0, 0.35)',
	},
	featurePillCopy: {
		columnGap: 4,
	},
	createGoldButton: {
		alignItems: 'center',
		justifyContent: 'center',
		flexDirection: 'row',
		columnGap: 8,
		borderWidth: 1,
		borderColor: '#FFF908',
		overflow: 'hidden',
		shadowColor: '#FF7200',
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: 0.7,
		shadowRadius: 9,
		elevation: 4,
	},
	createPlus: {
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: '#3D0A05',
	},
	createPlusText: {
		color: '#FFE45C',
		fontFamily: 'Sofia Sans',
		fontWeight: '800',
		textAlign: 'center',
		includeFontPadding: false,
	},
	createGoldText: {
		color: '#330E07',
		fontFamily: 'Sofia Sans',
		fontWeight: '800',
	},
	desktopRoot: {
		...StyleSheet.absoluteFillObject,
	},
	desktopColumns: {
		flexDirection: 'row',
	},
	desktopBar: {
		position: 'absolute',
		left: 0,
	},
	content: {
		alignItems: 'center',
	},
	carouselFallback: {
		flex: 1,
		alignItems: 'center',
		justifyContent: 'center',
	},
	emptyText: {
		color: '#FFFF99',
		fontFamily: 'Sofia Sans',
		fontWeight: '600',
	},
	createButton: {
		alignItems: 'center',
		justifyContent: 'center',
		flexDirection: 'row',
		columnGap: 6,
		borderRadius: 12,
		borderWidth: 1,
		borderColor: '#C4FF9B',
		backgroundColor: '#248F1C',
		shadowColor: '#83FF3C',
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: 0.6,
		shadowRadius: 9,
		elevation: 4,
	},
	createButtonText: {
		color: '#FFFFFF',
		fontFamily: 'Sofia Sans',
		fontWeight: '700',
	},
	invitationText: {
		color: '#FAE0A8',
		fontFamily: 'Sofia Sans',
		fontWeight: '500',
		textAlign: 'center',
	},
	featureRow: {
		flexDirection: 'row',
	},
	featureCard: {
		overflow: 'hidden',
		position: 'relative',
		borderRadius: 8,
		borderWidth: 1,
		borderColor: '#D66A16',
		backgroundColor: '#501006',
	},
	featureArtwork: {
		position: 'absolute',
		left: 0,
		zIndex: 1,
	},
	featureSurface: {
		position: 'absolute',
		zIndex: 0,
		backgroundColor: '#501006',
		shadowColor: '#FF7200',
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: 0.55,
		shadowRadius: 6,
		elevation: 3,
	},
	featureCopy: {
		position: 'absolute',
		zIndex: 2,
	},
	featureTitleRow: {
		alignItems: 'center',
		flexDirection: 'row',
		justifyContent: 'center',
	},
	featureTitle: {
		color: '#FFFF99',
		fontFamily: 'Sofia Sans',
		fontWeight: '700',
		textAlign: 'left',
	},
	featureSubtitle: {
		color: '#FFFFFF',
		fontFamily: 'Sofia Sans',
		fontWeight: '500',
		textAlign: 'center',
	},
});

export default VipRoomsScreen;
